import { useState, useEffect, useCallback } from 'react';
import { Activity, Filter, Maximize2, Minimize2, ChevronDown, Info } from 'lucide-react';
import apiClient from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';

const COLOR_LEGEND = [
  { label: 'Create / Login / Approve', color: '#16a34a', keywords: 'created, login, approved, linked, registered' },
  { label: 'Delete / Reject / Revoke', color: '#dc2626', keywords: 'deleted, rejected, removed, revoked' },
  { label: 'Update / Calculate', color: '#2563eb', keywords: 'updated, calculated, recalculated' },
  { label: 'Generate / Submit', color: '#9333ea', keywords: 'generated, submitted' },
  { label: 'Verify / Check', color: '#0891b2', keywords: 'verified, checked' },
  { label: 'System / Batch', color: '#d97706', keywords: 'batch, system, schedule' },
];

const getActionColor = (action) => {
  if (!action) return '#6B7280';
  if (/created|login|approved|linked|registered/.test(action)) return '#16a34a';
  if (/deleted|rejected|removed|revoked/.test(action)) return '#dc2626';
  if (/updated|calculated|recalculated/.test(action)) return '#2563eb';
  if (/generated|submitted/.test(action)) return '#9333ea';
  if (/verified|checked/.test(action)) return '#0891b2';
  if (/batch|system|schedule/.test(action)) return '#d97706';
  return '#6B7280';
};

const getEntityColor = (type) => {
  const map = { PPL: '#c026d3', ORG: '#0891b2', PRJ: '#2563eb', SYSTEM: '#d97706', API_KEY: '#16a34a', REG: '#C4A35A', SCR: '#16a34a', FIL: '#d97706', LST: '#9333ea' };
  return map[type] || '#9B9589';
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const ActivityPanel = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ action: '', entity_type: '', actor_id: '' });
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '500');
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.actor_id) params.append('actor_id', filters.actor_id);
      const { data } = await apiClient.get(`/activity-logs?${params}`);
      setLogs(data.items || []);
      setTotal(data.total || 0);
      setVisibleCount(50);
    } catch { toast.error('Failed to load activity logs'); }
    finally { setLoading(false); }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/activity-logs/stats?days=30');
      setStats(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchLogs(); fetchStats(); }, [fetchLogs, fetchStats]);

  const visibleLogs = logs.slice(0, visibleCount);

  return (
    <div className="flex flex-col" style={{ minHeight: expanded ? '85vh' : '50vh' }} data-testid="activity-panel">
      {/* Controls bar */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {/* Stats pills */}
        {stats && (
          <>
            <span className="px-3 py-1.5 text-[10px] font-mono font-bold border border-[#D8D3CB] bg-white text-[#0F1B2D]">
              30d: <span className="text-emerald-600">{stats.total_actions}</span>
            </span>
            <span className="px-3 py-1.5 text-[10px] font-mono font-bold border border-[#D8D3CB] bg-white text-[#0F1B2D]">
              Types: <span className="text-blue-600">{stats.by_action?.length || 0}</span>
            </span>
            <span className="px-3 py-1.5 text-[10px] font-mono font-bold border border-[#D8D3CB] bg-white text-[#0F1B2D]">
              Actors: <span className="text-purple-600">{stats.most_active_actors?.length || 0}</span>
            </span>
          </>
        )}
        <div className="ml-auto flex gap-2">
          <button onClick={() => setShowLegend(!showLegend)} className="px-3 py-1.5 text-[10px] font-mono font-bold border border-[#D8D3CB] hover:border-[#0F1B2D] transition-colors flex items-center gap-1" data-testid="activity-legend-toggle">
            <Info size={11} />{showLegend ? 'HIDE' : 'COLORS'}
          </button>
          <button onClick={() => setShowFilters(!showFilters)} className="px-3 py-1.5 text-[10px] font-mono font-bold border border-[#D8D3CB] hover:border-[#0F1B2D] transition-colors flex items-center gap-1" data-testid="activity-filter-toggle">
            <Filter size={11} />FILTER
          </button>
          <button onClick={() => setExpanded(!expanded)} className="px-3 py-1.5 text-[10px] font-mono font-bold border border-[#D8D3CB] hover:border-[#0F1B2D] transition-colors flex items-center gap-1" data-testid="activity-expand-toggle">
            {expanded ? <><Minimize2 size={11} />COLLAPSE</> : <><Maximize2 size={11} />EXPAND</>}
          </button>
        </div>
      </div>

      {/* Color legend */}
      {showLegend && (
        <div className="border border-[#D8D3CB] bg-white p-4 mb-4 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="activity-legend">
          {COLOR_LEGEND.map(({ label, color, keywords }) => (
            <div key={label} className="flex items-center gap-2">
              <div className="w-3 h-3 flex-shrink-0 border" style={{ backgroundColor: color }} />
              <div>
                <span className="text-[10px] font-mono font-bold text-[#0F1B2D]">{label}</span>
                <span className="text-[9px] font-mono text-[#9B9589] block">{keywords}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick action filters */}
      {stats?.by_action?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {stats.by_action.slice(0, 12).map(a => (
            <button key={a.action} onClick={() => setFilters(f => ({...f, action: f.action === a.action ? '' : a.action}))}
              className="px-2 py-1 text-[9px] font-mono font-bold border transition-all"
              style={{
                borderColor: filters.action === a.action ? getActionColor(a.action) : '#D8D3CB',
                backgroundColor: filters.action === a.action ? '#F8F7F5' : 'white',
                color: getActionColor(a.action),
              }}
            >
              {a.action} ({a.count})
            </button>
          ))}
        </div>
      )}

      {/* Filters panel */}
      {showFilters && (
        <div className="border border-[#D8D3CB] bg-white p-4 mb-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="activity-filters">
          <div>
            <label className="text-[9px] font-mono text-[#9B9589] uppercase block mb-1">entity type</label>
            <select value={filters.entity_type} onChange={(e) => setFilters(f => ({...f, entity_type: e.target.value}))}
              className="input-ainaa w-full h-8 text-xs px-2 font-mono" data-testid="filter-entity-type">
              <option value="">all</option>
              {['PPL', 'ORG', 'PRJ', 'SCR', 'FIL', 'LST', 'REG', 'TEAM', 'TASK', 'COLLAB', 'API_KEY', 'SYSTEM'].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[9px] font-mono text-[#9B9589] uppercase block mb-1">actor</label>
            <Input value={filters.actor_id} onChange={(e) => setFilters(f => ({...f, actor_id: e.target.value}))}
              className="h-8 text-xs font-mono" placeholder="email or ID" data-testid="filter-actor" />
          </div>
          <div className="flex items-end">
            <button onClick={() => setFilters({ action: '', entity_type: '', actor_id: '' })}
              className="px-3 py-1.5 text-[10px] font-mono font-bold text-red-500 border border-red-200 hover:bg-red-50 transition-colors" data-testid="filter-clear">CLEAR</button>
          </div>
        </div>
      )}

      {/* Log viewer — light mode */}
      <div
        className={`flex-1 border-2 border-[#D8D3CB] bg-white overflow-y-auto font-mono transition-all duration-300 ${expanded ? '' : 'max-h-[50vh]'}`}
        style={{ scrollbarWidth: 'thin' }}
        data-testid="activity-cmd-log"
      >
        {/* Header bar */}
        <div className="sticky top-0 z-10 bg-[#F8F7F5] border-b-2 border-[#D8D3CB] px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity size={14} className="text-[#C4A35A]" />
            <span className="text-[10px] font-bold text-[#0F1B2D] uppercase tracking-wider">Activity Log</span>
            <span className="text-[10px] text-[#9B9589]">{total} events</span>
          </div>
        </div>

        {/* Log lines */}
        <div className="divide-y divide-[#F0EDE8]">
          {loading ? (
            <div className="text-center py-12"><span className="text-[#9B9589] text-sm">Loading...</span></div>
          ) : visibleLogs.length === 0 ? (
            <div className="text-center py-12"><Activity className="w-8 h-8 text-[#D8D3CB] mx-auto mb-2" /><span className="text-[#9B9589] text-xs block">No activity logs found</span></div>
          ) : (
            <>
              {visibleLogs.map((log, i) => {
                const color = getActionColor(log.action);
                const entityColor = getEntityColor(log.entity_type);
                const meta = log.metadata ? Object.entries(log.metadata).filter(([k]) => !['_id'].includes(k)).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' ') : '';
                return (
                  <div key={log.id || i} className="flex gap-0 py-1.5 px-3 hover:bg-[#F8F7F5] group text-[11px] leading-5 items-start">
                    <span className="text-[#D8D3CB] select-none w-[34px] text-right pr-3 flex-shrink-0 text-[10px]">{i + 1}</span>
                    <span className="text-[#9B9589] flex-shrink-0 w-[130px]">{formatTimestamp(log.timestamp)}</span>
                    <span className="flex-shrink-0 w-[60px] text-[10px] font-bold" style={{ color: entityColor }}>{log.entity_type || 'SYS'}</span>
                    <span className="flex-shrink-0 font-bold" style={{ color }}>{log.action}</span>
                    {log.entity_id && <span className="ml-2 text-[#0891b2]">{log.entity_id}</span>}
                    {log.actor_id && <span className="ml-2 text-[#9333ea]">{log.actor_id}</span>}
                    {meta && <span className="ml-2 text-[#9B9589] opacity-50 group-hover:opacity-100 truncate">{meta}</span>}
                  </div>
                );
              })}
              {visibleCount < logs.length && (
                <div className="text-center py-3 border-t border-[#D8D3CB]">
                  <button
                    onClick={() => setVisibleCount(c => c + 50)}
                    className="px-5 py-2 text-[10px] font-mono font-bold text-[#0F1B2D] border border-[#D8D3CB] hover:border-[#0F1B2D] transition-colors inline-flex items-center gap-1"
                    data-testid="activity-show-more"
                  >
                    <ChevronDown size={12} />
                    SHOW MORE ({logs.length - visibleCount} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityPanel;
